import { useState, useMemo, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  BookOpen,
  Clock,
  Calendar,
  ArrowRight,
  ArrowLeft,
  X,
  Share2,
  CheckCircle2,
  Sparkles,
  ShoppingBag,
  Search,
  Check,
  Send,
} from "lucide-react";
import { useLanguage } from "../context/LanguageContext";
import { useToast } from "../context/ToastContext";
import { blogArticles } from "../data/blogData";
import { updateSEO } from "../utils/seo";
import "./Blog.css";

export default function Blog() {
  const { t, isRTL, language } = useLanguage();
  const { addToast } = useToast();

  const [activeCategory, setActiveCategory] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [newsletterEmail, setNewsletterEmail] = useState("");
  const [newsletterSubmitted, setNewsletterSubmitted] = useState(false);

  useEffect(() => {
    if (selectedArticle) {
      const artTitle = isRTL ? selectedArticle.title.ar : selectedArticle.title.en;
      const artDesc = isRTL ? selectedArticle.excerpt.ar : selectedArticle.excerpt.en;
      updateSEO({
        title: `${artTitle} | ${isRTL ? "مجلة تيري لاند" : "Terry Land Journal"}`,
        description: artDesc.slice(0, 160),
        canonical: `https://terryland.net/blog/${selectedArticle.slug}`,
        ogType: "article",
        ogImage: selectedArticle.coverImage,
        jsonLd: {
          "@context": "https://schema.org",
          "@type": "BlogPosting",
          "headline": artTitle,
          "description": artDesc,
          "image": selectedArticle.coverImage,
          "datePublished": selectedArticle.date,
          "author": {
            "@type": "Organization",
            "name": "Terry Land Atelier"
          },
          "publisher": {
            "@type": "Organization",
            "name": "Terry Land",
            "logo": {
              "@type": "ImageObject",
              "url": "https://terryland.net/logo512.png"
            }
          }
        }
      });
    } else {
      updateSEO({
        title: t("blog.metaTitle"),
        description: isRTL
          ? "استكشف أسرار المنسوجات الراقية، دلائل جهاز العروسة ومفروشات بيت الزوجية، ومقارنات الخامات بين القطن، المايكروفايبر، وخامة الوجهين المبتكرة."
          : "Explore fine textiles, essential bridal registry checklists, and expert comparisons between cotton, microfiber, and our breakthrough dual-face weaves.",
        canonical: "https://terryland.net/blog",
      });
    }
  }, [t, language, isRTL, selectedArticle]);

  // Lock body scroll when reader modal is open
  useEffect(() => {
    if (selectedArticle) {
      document.body.style.overflow = "hidden";
      const handleKeyDown = (e) => {
        if (e.key === "Escape") setSelectedArticle(null);
      };
      window.addEventListener("keydown", handleKeyDown);
      return () => {
        document.body.style.overflow = "";
        window.removeEventListener("keydown", handleKeyDown);
      };
    } else {
      document.body.style.overflow = "";
    }
  }, [selectedArticle]);

  const categories = [
    { id: "all", labelKey: "blog.catAll" },
    { id: "heritage", labelKey: "blog.catHeritage" },
    { id: "rituals", labelKey: "blog.catRituals" },
    { id: "guides", labelKey: "blog.catGuides" },
    { id: "care", labelKey: "blog.catCare" },
    { id: "interiors", labelKey: "blog.catInteriors" },
  ];

  // Featured article is the spotlight story
  const featuredArticle = useMemo(() => {
    return blogArticles.find((a) => a.featured) || blogArticles[0];
  }, []);

  // Filter remaining articles
  const filteredArticles = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return blogArticles.filter((article) => {
      // Category filter
      if (activeCategory !== "all" && article.category !== activeCategory) {
        return false;
      }

      // Search filter
      if (!q) return true;
      const titleAr = (article.title?.ar || "").toLowerCase();
      const titleEn = (article.title?.en || "").toLowerCase();
      const excerptAr = (article.excerpt?.ar || "").toLowerCase();
      const excerptEn = (article.excerpt?.en || "").toLowerCase();
      const bodyAr = (article.content?.ar?.sections || [])
        .map((s) => `${s.heading} ${s.body}`)
        .join(" ")
        .toLowerCase();
      const bodyEn = (article.content?.en?.sections || [])
        .map((s) => `${s.heading} ${s.body}`)
        .join(" ")
        .toLowerCase();

      return (
        titleAr.includes(q) ||
        titleEn.includes(q) ||
        excerptAr.includes(q) ||
        excerptEn.includes(q) ||
        bodyAr.includes(q) ||
        bodyEn.includes(q)
      );
    });
  }, [activeCategory, searchQuery]);

  const getCategoryLabel = (catId) => {
    const found = categories.find((c) => c.id === catId);
    return found ? t(found.labelKey) : catId;
  };

  const handleShare = async (article) => {
    const url = window.location.href;
    const title = isRTL ? article.title.ar : article.title.en;

    if (navigator.clipboard) {
      try {
        await navigator.clipboard.writeText(url);
        addToast(t("blog.copiedToast"), "success");
      } catch {
        addToast(url, "info");
      }
    } else {
      addToast(url, "info");
    }
  };

  const handleWhatsAppShare = (article) => {
    const title = isRTL ? article.title.ar : article.title.en;
    const url = window.location.href;
    const text = encodeURIComponent(`${title}\n${url}`);
    window.open(`https://wa.me/?text=${text}`, "_blank");
  };

  const handleNewsletterSubmit = (e) => {
    e.preventDefault();
    if (!newsletterEmail || !newsletterEmail.includes("@")) {
      addToast(
        isRTL
          ? "يرجى إدخال بريد إلكتروني صالح."
          : "Please enter a valid email address.",
        "error"
      );
      return;
    }
    setNewsletterSubmitted(true);
    addToast(t("blog.newsletterSuccess"), "success");
  };

  return (
    <div className="blog-page">
      {/* ── Hero Section ── */}
      <section className="blog-hero">
        <div className="blog-hero__glow" />
        <div className="container blog-hero__inner">
          <div className="blog-badge">
            <BookOpen size={14} className="blog-badge__icon" />
            <span>{t("blog.eyebrow")}</span>
          </div>

          <h1 className="blog-hero__title">{t("blog.title")}</h1>
          <p className="blog-hero__sub">{t("blog.subtitle")}</p>

          {/* Quick Search */}
          <div className="blog-search-wrap">
            <div className="blog-search-box">
              <Search size={16} className="blog-search-box__icon" />
              <input
                type="text"
                className="blog-search-box__input"
                placeholder={
                  isRTL
                    ? "ابحث في مقالات المجلة (مثال: جهاز العروسة، برانص، فوط، بشاكير، مايكروفايبر، قطن)..."
                    : "Search articles (e.g. bridal trousseau, bathrobes, towels, bath sheets, microfiber, cotton)..."
                }
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              {searchQuery && (
                <button
                  type="button"
                  className="blog-search-box__clear"
                  onClick={() => setSearchQuery("")}
                >
                  <X size={14} />
                </button>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* ── Featured Cover Story ── */}
      {featuredArticle && activeCategory === "all" && !searchQuery && (
        <section className="blog-featured-section">
          <div className="container">
            <article className="blog-featured-card">
              <div className="blog-featured-card__media">
                <img
                  src={featuredArticle.coverImage}
                  alt={
                    isRTL ? featuredArticle.title.ar : featuredArticle.title.en
                  }
                  className="blog-featured-card__img"
                  loading="lazy"
                />
                <div className="blog-featured-card__badge-overlay">
                  <Sparkles size={13} />
                  <span>{t("blog.featuredBadge")}</span>
                </div>
              </div>

              <div className="blog-featured-card__content">
                <div className="blog-meta-strip">
                  <span className="blog-meta-chip">
                    {getCategoryLabel(featuredArticle.category)}
                  </span>
                  <span className="blog-meta-dot">•</span>
                  <span className="blog-meta-time">
                    <Clock size={13} />
                    <span>
                      {t("blog.readTime").replace(
                        "{minutes}",
                        featuredArticle.readMinutes
                      )}
                    </span>
                  </span>
                </div>

                <h2 className="blog-featured-card__title">
                  {isRTL
                    ? featuredArticle.title.ar
                    : featuredArticle.title.en}
                </h2>

                <p className="blog-featured-card__excerpt">
                  {isRTL
                    ? featuredArticle.excerpt.ar
                    : featuredArticle.excerpt.en}
                </p>

                <div className="blog-featured-card__footer">
                  <div className="blog-author-tag">
                    <div className="blog-author-avatar">TL</div>
                    <div className="blog-author-info">
                      <span className="blog-author-name">
                        {t("blog.atelierAuthor")}
                      </span>
                      <span className="blog-author-date">
                        {featuredArticle.date}
                      </span>
                    </div>
                  </div>

                  <button
                    type="button"
                    className="btn-primary blog-read-btn"
                    onClick={() => setSelectedArticle(featuredArticle)}
                  >
                    <span>{t("blog.readStory")}</span>
                    {isRTL ? (
                      <ArrowLeft size={16} className="rtl-flip" />
                    ) : (
                      <ArrowRight size={16} className="rtl-flip" />
                    )}
                  </button>
                </div>
              </div>
            </article>
          </div>
        </section>
      )}

      {/* ── Category Tabs Bar ── */}
      <section className="blog-filter-section">
        <div className="container">
          <div className="blog-categories-scroll">
            <div className="blog-categories">
              {categories.map((cat) => {
                const isActive = activeCategory === cat.id;
                const count =
                  cat.id === "all"
                    ? blogArticles.length
                    : blogArticles.filter((a) => a.category === cat.id).length;

                return (
                  <button
                    key={cat.id}
                    type="button"
                    className={`blog-cat-btn ${
                      isActive ? "blog-cat-btn--active" : ""
                    }`}
                    onClick={() => setActiveCategory(cat.id)}
                  >
                    <span>{t(cat.labelKey)}</span>
                    <span className="blog-cat-btn__count">{count}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* ── Articles Grid ── */}
      <section className="blog-grid-section">
        <div className="container">
          {filteredArticles.length > 0 ? (
            <div className="blog-grid">
              {filteredArticles.map((article) => {
                const titleText = isRTL ? article.title.ar : article.title.en;
                const excerptText = isRTL
                  ? article.excerpt.ar
                  : article.excerpt.en;

                return (
                  <article key={article.id} className="blog-card">
                    <div
                      className="blog-card__media"
                      onClick={() => setSelectedArticle(article)}
                      role="button"
                      tabIndex={0}
                      aria-label={titleText}
                    >
                      <img
                        src={article.coverImage}
                        alt={titleText}
                        className="blog-card__img"
                        loading="lazy"
                      />
                      <span className="blog-card__category">
                        {getCategoryLabel(article.category)}
                      </span>
                    </div>

                    <div className="blog-card__body">
                      <div className="blog-card__meta">
                        <span className="blog-card__meta-item">
                          <Calendar size={13} />
                          <span>{article.date}</span>
                        </span>
                        <span className="blog-meta-dot">•</span>
                        <span className="blog-card__meta-item">
                          <Clock size={13} />
                          <span>
                            {t("blog.readTime").replace(
                              "{minutes}",
                              article.readMinutes
                            )}
                          </span>
                        </span>
                      </div>

                      <h3
                        className="blog-card__title"
                        onClick={() => setSelectedArticle(article)}
                      >
                        {titleText}
                      </h3>

                      <p className="blog-card__excerpt">{excerptText}</p>

                      <div className="blog-card__footer">
                        <button
                          type="button"
                          className="blog-card__link"
                          onClick={() => setSelectedArticle(article)}
                        >
                          <span>{t("blog.readStory")}</span>
                          {isRTL ? (
                            <ArrowLeft size={15} className="rtl-flip" />
                          ) : (
                            <ArrowRight size={15} className="rtl-flip" />
                          )}
                        </button>
                      </div>
                    </div>
                  </article>
                );
              })}
            </div>
          ) : (
            <div className="blog-empty">
              <BookOpen size={44} className="blog-empty__icon" />
              <h3>
                {isRTL
                  ? "لم يتم العثور على مقالات تطابق بحثك"
                  : "No stories found matching your search"}
              </h3>
              <p>
                {isRTL
                  ? "جرب استخدام كلمات بحث مختلفة أو تصفح جميع المقالات."
                  : "Try different keywords or browse all categories."}
              </p>
              <button
                type="button"
                className="btn-outline"
                onClick={() => {
                  setSearchQuery("");
                  setActiveCategory("all");
                }}
              >
                {isRTL ? "إعادة تعيين التصفية" : "Reset Filter"}
              </button>
            </div>
          )}
        </div>
      </section>

      {/* ── Newsletter Atelier Circle Banner ── */}
      <section className="blog-newsletter-section">
        <div className="container">
          <div className="blog-newsletter-card">
            <div className="blog-newsletter-card__content">
              <span className="blog-newsletter-badge">
                <Sparkles size={13} />
                <span>{t("blog.eyebrow")}</span>
              </span>
              <h2 className="blog-newsletter-title">
                {t("blog.newsletterTitle")}
              </h2>
              <p className="blog-newsletter-sub">{t("blog.newsletterSub")}</p>

              {newsletterSubmitted ? (
                <div className="blog-newsletter-success">
                  <CheckCircle2 size={18} />
                  <span>{t("blog.newsletterSuccess")}</span>
                </div>
              ) : (
                <form
                  onSubmit={handleNewsletterSubmit}
                  className="blog-newsletter-form"
                >
                  <input
                    type="email"
                    className="blog-newsletter-input"
                    placeholder={t("blog.newsletterPlaceholder")}
                    value={newsletterEmail}
                    onChange={(e) => setNewsletterEmail(e.target.value)}
                    required
                  />
                  <button type="submit" className="blog-newsletter-btn">
                    <span>{t("blog.newsletterSubscribe")}</span>
                    <Send size={15} />
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* ── Full Article Reader Modal ── */}
      {selectedArticle && (
        <div
          className="blog-modal-backdrop"
          onClick={() => setSelectedArticle(null)}
          role="dialog"
          aria-modal="true"
        >
          <div
            className="blog-modal-sheet"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="blog-modal-header">
              <div className="blog-modal-header__meta">
                <span className="blog-meta-chip">
                  {getCategoryLabel(selectedArticle.category)}
                </span>
                <span className="blog-meta-dot">•</span>
                <span className="blog-meta-time">
                  <Clock size={13} />
                  <span>
                    {t("blog.readTime").replace(
                      "{minutes}",
                      selectedArticle.readMinutes
                    )}
                  </span>
                </span>
                <span className="blog-meta-dot">•</span>
                <span className="blog-modal-date">{selectedArticle.date}</span>
              </div>

              <div className="blog-modal-header__actions">
                <button
                  type="button"
                  className="blog-action-icon-btn"
                  onClick={() => handleShare(selectedArticle)}
                  title={t("blog.shareStory")}
                  aria-label={t("blog.shareStory")}
                >
                  <Share2 size={18} />
                </button>
                <button
                  type="button"
                  className="blog-action-icon-btn blog-action-icon-btn--close"
                  onClick={() => setSelectedArticle(null)}
                  title={t("blog.closeReader")}
                  aria-label={t("blog.closeReader")}
                >
                  <X size={20} />
                </button>
              </div>
            </div>

            {/* Modal Scrollable Body */}
            <div className="blog-modal-body">
              {/* Media Hero */}
              <div className="blog-modal-media">
                <img
                  src={selectedArticle.coverImage}
                  alt={
                    isRTL
                      ? selectedArticle.title.ar
                      : selectedArticle.title.en
                  }
                  className="blog-modal-media__img"
                />
              </div>

              {/* Title & Author */}
              <h1 className="blog-modal-title">
                {isRTL
                  ? selectedArticle.title.ar
                  : selectedArticle.title.en}
              </h1>

              <div className="blog-modal-author">
                <div className="blog-author-avatar">TL</div>
                <div className="blog-author-info">
                  <span className="blog-author-name">
                    {t("blog.atelierAuthor")}
                  </span>
                  <span className="blog-author-role">
                    {isRTL
                      ? " تيري لاند، القاهرة"
                      : "Terry Land, Cairo"}
                  </span>
                </div>
              </div>

              {/* Formatted Content */}
              {(() => {
                const articleLang = isRTL
                  ? selectedArticle.content.ar
                  : selectedArticle.content.en;

                return (
                  <div className="blog-modal-article">
                    <p className="blog-modal-intro">{articleLang.intro}</p>

                    {articleLang.sections.map((section, idx) => (
                      <div key={idx} className="blog-modal-section">
                        <h2 className="blog-modal-section__heading">
                          {section.heading}
                        </h2>
                        <p className="blog-modal-section__body">
                          {section.body}
                        </p>
                      </div>
                    ))}

                    {/* Pull Quote */}
                    {articleLang.quote && (
                      <blockquote className="blog-modal-quote">
                        <p>“{articleLang.quote}”</p>
                      </blockquote>
                    )}

                    {/* Key Takeaways */}
                    {articleLang.keyTakeaways && (
                      <div className="blog-modal-takeaways">
                        <h3 className="blog-takeaways-title">
                          <CheckCircle2 size={16} />
                          <span>{t("blog.keyTakeaways")}</span>
                        </h3>
                        <ul className="blog-takeaways-list">
                          {articleLang.keyTakeaways.map((point, pIdx) => (
                            <li key={pIdx}>
                              <Check size={14} className="blog-takeaways-check" />
                              <span>{point}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* In-Article Shop CTA */}
                    {articleLang.recommendedCollectionLink && (
                      <div className="blog-modal-shop-box">
                        <div className="blog-shop-box__icon">
                          <ShoppingBag size={24} />
                        </div>
                        <div className="blog-shop-box__text">
                          <span className="blog-shop-box__label">
                            {t("blog.shopRecommended")}
                          </span>
                          <h4 className="blog-shop-box__name">
                            {articleLang.recommendedCollectionName}
                          </h4>
                        </div>
                        <Link
                          to={articleLang.recommendedCollectionLink}
                          className="btn-primary blog-shop-box__btn"
                          onClick={() => setSelectedArticle(null)}
                        >
                          <span>{t("blog.exploreCollection")}</span>
                          {isRTL ? (
                            <ArrowLeft size={16} className="rtl-flip" />
                          ) : (
                            <ArrowRight size={16} className="rtl-flip" />
                          )}
                        </Link>
                      </div>
                    )}
                  </div>
                );
              })()}

              {/* Modal Footer Controls */}
              <div className="blog-modal-footer">
                <button
                  type="button"
                  className="btn-outline blog-footer-share-btn"
                  onClick={() => handleWhatsAppShare(selectedArticle)}
                >
                  <Share2 size={15} />
                  <span>
                    {isRTL
                      ? "مشاركة عبر واتساب"
                      : "Share via WhatsApp"}
                  </span>
                </button>
                <button
                  type="button"
                  className="btn-outline blog-footer-close-btn"
                  onClick={() => setSelectedArticle(null)}
                >
                  {t("blog.closeReader")}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
